import CheckIcon from '@mui/icons-material/Check'
import MinusIcon from '@mui/icons-material/Remove'
import { useCallback, useMemo, useRef } from 'react'
import DPFPriceImg1 from '../assets/landing/DPF-price-img-1.png'
import PlusIcon from '../assets/landing/plus-icon.svg?react'
import PremiumIconWhite from '../assets/landing/premium-icon-white.svg?react'
import PremiumIcon from '../assets/landing/premium-icon.svg?react'

type TableCellValue =
  | { type: 'text'; content: React.ReactNode; color?: 'default' | 'premium' }
  | { type: 'check'; color?: 'default' | 'premium' }
  | { type: 'minus' }
  | { type: 'list'; items: string[]; color?: 'default' | 'premium' }

interface TableFeature {
  name: React.ReactNode
  free: TableCellValue
  premium: TableCellValue
  isFirstRow?: boolean
  isLastRow?: boolean
}

export default function ProductPricingSection({
  isPremiumUser,
  premiumDeepLinkUrl,
  logClickedLearnMoreAboutPremium,
}: {
  isPremiumUser: boolean | null
  premiumDeepLinkUrl: string
  logClickedLearnMoreAboutPremium: () => void
}) {
  const deeplinkAnchorRef = useRef<HTMLAnchorElement>(null)

  const tableFeatures = useMemo<TableFeature[]>(
    () => [
      {
        name: '価格 (税込)',
        free: { type: 'text', content: '無料' },
        premium: { type: 'text', content: '¥590 / 月〜', color: 'premium' },
        isFirstRow: true,
      },
      {
        name: (
          <>
            表示できる
            <br />
            写真・動画の数
          </>
        ),
        free: {
          type: 'text',
          content: (
            <>
              おすすめの写真
              <br />
              11枚まで
            </>
          ),
        },
        premium: { type: 'text', content: '無制限※', color: 'premium' },
      },
      {
        name: '動画の表示',
        free: { type: 'minus' },
        premium: { type: 'check', color: 'premium' },
      },
      {
        name: (
          <>
            表示内容の
            <br />
            絞り込み
          </>
        ),
        free: { type: 'minus' },
        premium: { type: 'list', items: ['お気に入り', '期間', '公開範囲'], color: 'premium' },
      },
      {
        name: '複数アルバムの表示',
        free: { type: 'check' },
        premium: { type: 'check', color: 'premium' },
      },
      {
        name: 'みてねアプリからリモート設定',
        free: { type: 'check' },
        premium: { type: 'check', color: 'premium' },
        isLastRow: true,
      },
    ],
    [],
  )

  const renderCell = useCallback((value: TableCellValue) => {
    switch (value.type) {
      case 'text': {
        const textColor = value.color === 'premium' ? 'text-[#EE5F87]' : 'text-gray-12'
        return <span className={textColor}>{value.content}</span>
      }
      case 'check': {
        const checkColor = value.color === 'premium' ? 'text-[#EE5F87]' : 'text-gray-12'
        return <CheckIcon className={`w-4 h-4 ${checkColor} mx-auto`} />
      }
      case 'minus':
        return <MinusIcon className='w-4 h-4 text-gray-12 mx-auto' />
      case 'list':
        return (
          <div className='flex justify-center'>
            <ul
              className={`list-none space-y-1 text-left ${value.color === 'premium' ? 'text-[#EE5F87]' : 'text-gray-12'}`}
            >
              {value.items.map((item, idx) => (
                <li key={idx}>・{item}</li>
              ))}
            </ul>
          </div>
        )
    }
  }, [])

  const getRowClasses = useCallback((feature: TableFeature) => {
    return `bg-white ${feature.isLastRow ? '' : 'border-b border-gray-100'}`
  }, [])

  const getFeatureCellClasses = useCallback((feature: TableFeature) => {
    const base = 'p-4 text-xs text-gray-12 border-r border-gray-100'
    if (feature.isFirstRow) {
      return `${base} rounded-tl-lg`
    }
    if (feature.isLastRow) {
      return `${base} rounded-bl-lg`
    }
    return base
  }, [])

  const getFreeCellClasses = useCallback(() => {
    return 'p-4 text-xs text-gray-12 font-semibold text-center border-r border-gray-100'
  }, [])

  const getPremiumCellClasses = useCallback((feature: TableFeature) => {
    const base = 'p-4 text-sm font-semibold text-center'
    if (feature.isLastRow) {
      return `${base} rounded-br-lg`
    }
    return base
  }, [])

  const onLearnMoreAboutPremiumClick = useCallback(() => {
    logClickedLearnMoreAboutPremium()
    deeplinkAnchorRef.current?.click()
  }, [logClickedLearnMoreAboutPremium])

  return (
    <section className='px-4 py-8 space-y-6 bg-[#F5F5F5]'>
      <div className='space-y-4'>
        <h2 className='text-2xl font-semibold text-gray-12 text-center mb-6'>価格について</h2>
        {isPremiumUser ? (
          <>
            <div className='w-full py-2 bg-[#FB6B92] rounded-lg'>
              <div className='text-center text-white text-sm font-semibold'>
                プレミアムプラン契約中のため
                <br />
                すべての機能をお使いいただけます！
              </div>
            </div>
            <div className='text-base text-gray-12 leading-[160%] tracking-0'>
              <p className='font-semibold'>
                本体を購入するだけで、すべての写真・動画(※)が無制限に表示されます。
              </p>
              <p className='font-normal'>
                ※プレミアムまたはProプランを解約すると、みてねおすすめの11枚の写真が毎月表示されるようになります。
              </p>
            </div>
          </>
        ) : (
          <div className='text-base font-normal text-gray-12 leading-[160%] tracking-0'>
            <p>
              本体を購入するだけで、みてねより自動で提案される11枚の写真が毎月表示されます。おすすめの写真は毎月変わります。
            </p>
            <p>
              プレミアムまたはProプランにご登録いただくと、みてねに保存されたすべての写真と動画が無制限に表示されます。
            </p>
          </div>
        )}
      </div>

      <div className='flex flex-col gap-4 bg-white rounded-lg p-4'>
        <div>
          <img
            src={DPFPriceImg1}
            alt='Digital Photo Frame Product'
            className='w-full rounded-lg'
            loading='lazy'
          />
        </div>

        <div className='space-y-2 text-gray-12'>
          <h3 className='text-xl font-semibold leading-[140%] tracking-0'>本体価格</h3>
          <p className='text-xl font-semibold leading-[140%] tracking-0'>16,500円(税込)</p>
          <div className='space-y-1 text-base font-normal leading-[160%] tracking-0'>
            <p>パッケージ内容:</p>
            <p>本体/電源ケーブル/サポートスタンド/かんたんセットアップガイド</p>
          </div>
        </div>
      </div>

      <PlusIcon className='w-8 h-8 mx-auto' />

      {!isPremiumUser && (
        <>
          <div className='space-y-4 text-center text-[#EE5F87]'>
            <div className='flex flex-col gap-6'>
              <div className='flex-1'>
                <p className='text-xl font-semibold'>
                  みてねプレミアムにご登録で
                  <br />
                  すべての機能が使えるようになります
                </p>
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-4 bg-white rounded-lg p-4'>
            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <PremiumIcon className='w-5 h-5 text-gray-12 flex-shrink-0' />
                <h4 className='text-xl font-semibold text-gray-12'>みてねプレミアム</h4>
              </div>
              <p className='text-xl font-semibold text-gray-12'>590円(税込)/月~</p>
              <div className='text-base font-normal text-gray-12 leading-[160%] tracking-0'>
                <p>
                  みてねにある写真・動画をすべて表示できるなど、フォトフレームの機能を無制限でお使いいただけます。
                </p>
                <p>他にもみてねアプリで使える便利で楽しい機能がたくさん！</p>
              </div>
            </div>
            <a
              ref={deeplinkAnchorRef}
              href={premiumDeepLinkUrl}
              className='hidden'
              aria-hidden='true'
            />
            <button
              className='py-2 px-8 mx-auto block bg-[#F5F5F5] text-blue-10 rounded-[20px] font-semibold text-sm'
              onClick={onLearnMoreAboutPremiumClick}
            >
              プレミアムについて詳しくみる
            </button>
          </div>
        </>
      )}

      <div className='space-y-6'>
        <h2 className='text-xl font-semibold text-gray-12 text-center'>フォトフレームの機能比較</h2>
        <div className='overflow-x-auto'>
          <table className='w-full border-collapse'>
            <thead>
              <tr>
                <th className='w-1/3'></th>
                <th className='w-1/3 bg-[#646464] text-white p-3 text-center text-sm font-semibold rounded-tl-lg'>
                  無料
                </th>
                <th className='w-1/3 bg-[#EE5F87] text-white p-3 text-center text-sm font-bold relative rounded-tr-lg'>
                  <div className='flex items-center justify-center gap-1'>
                    <PremiumIconWhite className='w-5 h-5 ' />
                    <span className='text-sm font-semibold'>プレミアム</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className='rounded-lg overflow-hidden text-center'>
              {tableFeatures.map((feature, index) => (
                <tr key={index} className={getRowClasses(feature)}>
                  <td className={getFeatureCellClasses(feature)}>{feature.name}</td>
                  <td className={getFreeCellClasses()}>{renderCell(feature.free)}</td>
                  <td className={getPremiumCellClasses(feature)}>{renderCell(feature.premium)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className='text-sm text-gray-12 text-left leading-relaxed font-normal'>
          ※写真・動画の枚数が著しく多い一部のユーザーは制限を受けることがあります
        </p>
      </div>
    </section>
  )
}
